import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUp, Cpu, Trophy, Users } from 'lucide-react'
import { cn } from '@/lib/utils'
import BrandLogo from '@/components/landing/brand-logo'
import { neonButtonClasses } from '@/components/landing/neon-link'
import { ARENAS } from '@/constants/landing'
import { playHref } from '@/constants/play'

const PLAY_LINKS = [
    { label: 'Play the computer', href: playHref('computer') },
    { label: 'Pass and play', href: playHref('friend') },
    { label: 'Featured puzzle', href: '/#puzzle' },
    { label: 'Board themes', href: '/#themes' }
]

const EXPLORE_LINKS = [
    { label: 'Features', href: '/#features' },
    { label: 'Time controls', href: '/#modes' },
    { label: 'Pricing', href: '/#pricing' },
    { label: 'FAQ', href: '/#faq' }
]

const linkClass = 'rounded text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon-cyan'
const headingClass = 'font-mono text-[11px] font-semibold uppercase tracking-[0.26em]'

const pad = (value: number): string => String(value).padStart(2, '0')

const SiteFooter: FC = (): ReactNode => {
    const scrollToTop = () => {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' })
    }

    return (
        <footer className='relative overflow-hidden border-t border-white/5 bg-[#04040b]'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-neon-pink/70 to-transparent' />
            <div aria-hidden='true' className='pointer-events-none absolute left-1/2 top-0 h-48 w-2/3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-neon-pink/15 blur-[110px]' />
            <div aria-hidden='true' className='pointer-events-none absolute -bottom-20 left-0 h-64 w-1/2 rounded-full bg-neon-cyan/10 blur-[120px]' />

            <div className='relative mx-auto max-w-7xl px-5 pt-16 sm:px-8 lg:pt-20'>
                <div className='grid gap-12 lg:grid-cols-[1.25fr_2fr] lg:gap-16'>
                    <div className='max-w-sm'>
                        <BrandLogo />

                        <p className='mt-5 text-sm leading-relaxed text-muted-foreground'>
                            Chess that glows in the dark. Fast games, sharp puzzles and nightly arenas for players who do their best thinking after sundown.
                        </p>

                        <div className='mt-7 flex flex-wrap gap-3'>
                            <Link href={playHref('computer')} className={neonButtonClasses('primary', 'sm')}>
                                <Cpu className='size-4' />
                                Play the computer
                            </Link>

                            <Link href={playHref('friend')} className={neonButtonClasses('outline', 'sm')}>
                                <Users className='size-4' />
                                Pass and play
                            </Link>
                        </div>
                    </div>

                    <div className='grid gap-10 sm:grid-cols-3'>
                        <nav aria-label='Play'>
                            <p className={cn(headingClass, 'text-neon-cyan')}>
                                Play
                            </p>

                            <ul className='mt-5 space-y-3'>
                                {PLAY_LINKS.map(link => (
                                    <li key={link.label}>
                                        <Link href={link.href} className={linkClass}>
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </nav>

                        <nav aria-label='Explore'>
                            <p className={cn(headingClass, 'text-neon-violet')}>
                                Explore
                            </p>

                            <ul className='mt-5 space-y-3'>
                                {EXPLORE_LINKS.map(link => (
                                    <li key={link.label}>
                                        <a href={link.href} className={linkClass}>
                                            {link.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </nav>

                        <div>
                            <p className={cn(headingClass, 'text-neon-amber')}>
                                Arenas this week
                            </p>

                            <ul className='mt-5 space-y-3'>
                                {ARENAS.map(arena => (
                                    <li key={arena.name}>
                                        <a href='/#arenas' className='group flex items-baseline gap-3 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon-cyan'>
                                            <span className='w-16 shrink-0 font-mono text-xs text-muted-foreground/70'>
                                                {arena.day.slice(0, 3)} {pad(arena.hourUtc)}:00
                                            </span>

                                            <span className={cn('flex items-center gap-1.5 text-sm transition-colors', arena.featured ? 'text-neon-pink' : 'text-muted-foreground group-hover:text-foreground')}>
                                                {arena.name}
                                                {arena.featured && <Trophy className='size-3.5' />}
                                            </span>
                                        </a>
                                    </li>
                                ))}
                            </ul>

                            <p className='mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground/60'>
                                All times UTC
                            </p>
                        </div>
                    </div>
                </div>

                <div className='mt-14 flex flex-col gap-4 border-t border-white/5 py-6 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        © <span suppressHydrationWarning>{new Date().getFullYear()}</span> Neon Gambit. All moves reserved.
                    </p>

                    <p className='font-mono text-muted-foreground/70'>
                        1. e4 · Your move.
                    </p>

                    <button
                        type='button'
                        onClick={scrollToTop}
                        className='inline-flex items-center gap-2 self-start rounded-full border border-white/10 px-3.5 py-1.5 transition-colors hover:border-white/30 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon-cyan sm:self-auto'
                    >
                        <ArrowUp className='size-3.5' />
                        Back to top
                    </button>
                </div>
            </div>

            <div aria-hidden='true' className='relative select-none px-[1vw] pb-[1vw]'>
                <svg viewBox='0 0 1000 118' className='block h-auto w-full overflow-visible'>
                    <defs>
                        <linearGradient id='neonchess-fill' x1='0' x2='1' y1='0' y2='0'>
                            <stop offset='0%' stopColor='#2ef2ff' />
                            <stop offset='50%' stopColor='#9b6bff' />
                            <stop offset='100%' stopColor='#ff3dce' />
                        </linearGradient>

                        <filter id='neonchess-glow' x='-10%' y='-40%' width='120%' height='180%'>
                            <feGaussianBlur stdDeviation='9' />
                        </filter>
                    </defs>

                    <text
                        x='2'
                        y='110'
                        textLength='996'
                        lengthAdjust='spacingAndGlyphs'
                        fontSize='140'
                        fontWeight='800'
                        className='font-heading'
                        fill='url(#neonchess-fill)'
                        filter='url(#neonchess-glow)'
                        opacity='0.55'
                    >
                        NEONCHESS
                    </text>

                    <text
                        x='2'
                        y='110'
                        textLength='996'
                        lengthAdjust='spacingAndGlyphs'
                        fontSize='140'
                        fontWeight='800'
                        className='font-heading'
                        fill='url(#neonchess-fill)'
                    >
                        NEONCHESS
                    </text>
                </svg>
            </div>
        </footer>
    )
}

export default SiteFooter
